/**
 * Lab `Member/searchHierarchyPage` — Hierarchy Search chain table.
 * CM is Company Master: columns Player → Master → SM → DM → TM.
 * Scope is always the caller's downline (`ancestor_id = caller` on CM-API).
 *
 * CM-API page size is hardcoded 50 — UI Pager must match (`HIERARCHY_SEARCH_PAGE_SIZE`).
 */

import { authHeaders } from '@/auth/session'
import { cmApi, unwrapCmDataRecord } from '@/api/cmApi'

/** Matches CM-API `member.service` `PAGE_SIZE` for searchHierarchyPage. */
export const HIERARCHY_SEARCH_PAGE_SIZE = 50

const MEMBER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Hierarchy Search chip is a member UUID → Lab `serchById` path. */
export function isHierarchySearchMemberId(raw: string): boolean {
  return MEMBER_ID_RE.test(raw.trim())
}

/** Lab request — keep `serchById` spelling. */
export type SearchHierarchyPageRequest = {
  filter?: string | string[]
  page?: number
  serchById?: boolean
}

export type HierarchySearchRow = {
  _id: string
  username: string
  accessLevel: string
  Player?: string
  PlayerId?: string
  PlayerparentId?: string
  PlayerCreatedAt?: string
  Master?: string
  MasterId?: string
  MasterCreatedAt?: string
  SuperMaster?: string
  SuperMasterId?: string
  SuperMasterCreatedAt?: string
  DoubleMaster?: string
  DoubleMasterId?: string
  DoubleMasterCreatedAt?: string
  TripleMaster?: string
  TripleMasterId?: string
  TripleMasterCreatedAt?: string
}

const LEVEL_KEYS = [
  'Player',
  'Master',
  'SuperMaster',
  'DoubleMaster',
  'TripleMaster',
] as const

export type HierarchyColumnId = (typeof LEVEL_KEYS)[number]

/** Lab hierarchy-search.html columns for Company Master. */
export const CM_HIERARCHY_COLUMNS: {
  id: HierarchyColumnId
  header: string
  short: string
}[] = [
  { id: 'Player', header: 'Player (P/L)', short: 'P' },
  { id: 'Master', header: 'Master (P/L)', short: 'M' },
  { id: 'SuperMaster', header: 'SM (P/L)', short: 'SM' },
  { id: 'DoubleMaster', header: 'DM (P/L)', short: 'DM' },
  { id: 'TripleMaster', header: 'TM (P/L)', short: 'TM' },
]

export function normalizeHierarchySearchRow(
  raw: Record<string, unknown>,
): HierarchySearchRow | null {
  const id = String(raw._id ?? raw.id ?? '').trim()
  const username = String(raw.username ?? '').trim()
  if (!id && !username) return null

  const accessLevel = String(raw.accessLevel ?? '').trim() || inferAccessLevel(raw)
  const row: HierarchySearchRow = {
    _id: id || username,
    username: username || id,
    accessLevel,
  }

  for (const key of LEVEL_KEYS) {
    const name = raw[key]
    if (typeof name === 'string' && name.trim()) {
      row[key] = name.trim()
    }
    const idVal = raw[`${key}Id`]
    if (typeof idVal === 'string' && idVal.trim()) {
      ;(row as Record<string, string>)[`${key}Id`] = idVal.trim()
    }
    const created = raw[`${key}CreatedAt`]
    if (typeof created === 'string' && created.trim()) {
      ;(row as Record<string, string>)[`${key}CreatedAt`] = created.trim()
    }
  }

  if (typeof raw.PlayerparentId === 'string' && raw.PlayerparentId.trim()) {
    row.PlayerparentId = raw.PlayerparentId.trim()
  }

  if (!row.Player && row.username && (accessLevel === 'Player' || !accessLevel)) {
    row.Player = row.username
  }

  return row
}

function inferAccessLevel(raw: Record<string, unknown>): string {
  for (const key of LEVEL_KEYS) {
    if (typeof raw[key] === 'string' && raw[key]) return key
  }
  return 'Player'
}

export function normalizeHierarchySearchRows(list: unknown[]): HierarchySearchRow[] {
  const out: HierarchySearchRow[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    const row = normalizeHierarchySearchRow(item as Record<string, unknown>)
    if (row) out.push(row)
  }
  return out
}

function pagePayloadToResult(page: Record<string, unknown>): {
  rows: HierarchySearchRow[]
  total: number
} {
  const list = Array.isArray(page.data) ? page.data : []
  const rows = normalizeHierarchySearchRows(list)
  const total =
    typeof page.total === 'number' && Number.isFinite(page.total) ? page.total : rows.length
  return { rows, total }
}

/**
 * Map CM-API Hierarchy Search response → rows.
 * Prefer Standard `{ status, message, data }`; Lab `{ value, data }` only as shim.
 */
export function mapSearchHierarchyPageResponse(res: unknown): {
  rows: HierarchySearchRow[]
  total: number
} {
  if (!res || typeof res !== 'object') return { rows: [], total: 0 }
  const o = res as Record<string, unknown>

  if (typeof o.status === 'number') {
    if (o.status !== 200) return { rows: [], total: 0 }
    return pagePayloadToResult(unwrapCmDataRecord(res))
  }

  // Lab `{ value, data: { data, total } }` shim (legacy until all paths Standard).
  if ('value' in o) {
    if (o.value === false) return { rows: [], total: 0 }
    const page =
      o.data != null && typeof o.data === 'object' && !Array.isArray(o.data)
        ? (o.data as Record<string, unknown>)
        : {}
    return pagePayloadToResult(page)
  }

  return pagePayloadToResult(unwrapCmDataRecord(res))
}

export async function searchHierarchyPage(
  body: SearchHierarchyPageRequest,
): Promise<{ rows: HierarchySearchRow[]; total: number }> {
  const res = await cmApi.searchHierarchyPage(authHeaders(), body as Record<string, unknown>)
  return mapSearchHierarchyPageResponse(res)
}
